import { isResource } from '@medplum/core'
import type {
  Annotation,
  CodeableConcept,
  Extension as FHIRExtension,
  Immunization,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Reference,
} from '@medplum/fhirtypes'
import { addHours } from 'date-fns'
import type { CodingValue } from '#src/codeableConcept.ts'
import { type DateString, isDateString, startOfDateInUTC } from '#src/dates.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  IMMUNIZATION_ALLOWED_SHARING_EXTENSION,
  IMMUNIZATION_BARCODE_EXTENSION,
  IMMUNIZATION_CAIR_SUBMISSION_EXTENSION,
  IMMUNIZATION_DOSE_VALIDITY_EXTENSION,
  IMMUNIZATION_DOSE_VALIDITY_REASON_EXTENSION,
  IMMUNIZATION_ENTERED_IN_ERROR_REASON_EXTENSION,
  IMMUNIZATION_FUNDING_SOURCE_EXTENSION,
  IMMUNIZATION_INFORMATION_SOURCE_EXTENSION,
  IMMUNIZATION_NDC_EXTENSION,
  IMMUNIZATION_PATIENT_CONSENT_EXTENSION,
  IMMUNIZATION_PRIORITY_POPULATION_EXTENSION,
  IMMUNIZATION_PUBLICITY_CODE_EXTENSION,
  IMMUNIZATION_RAW_HL7_EXTENSION,
  IMMUNIZATION_SERIES_EXTENSION,
  IMMUNIZATION_VFC_ELIGIBILITY_EXTENSION,
} from '#src/structureDefinitions.ts'
import { isOfType } from '#src/utils.ts'

function isEnteredInErrorReason(note: Annotation): boolean {
  return (
    note.extension?.some(
      (e) =>
        e.url === IMMUNIZATION_ENTERED_IN_ERROR_REASON_EXTENSION &&
        e.valueBoolean === true
    ) ?? false
  )
}

export class ExtendedImmunization extends ExtendedResource<Immunization> {
  static readonly RESOURCE_TYPE = 'Immunization'

  static byCodeAndDate({
    code,
    date,
    patient,
  }: {
    code: CodingValue
    date: string
    patient: Reference<Patient>
  }): QueryTypes {
    return {
      'vaccine-code': code,
      ...this.byDate({ date, patient }),
    }
  }

  static byDate({
    date,
    patient,
  }: {
    date: string
    patient: Reference<Patient>
  }): QueryTypes {
    // The window is the UTC calendar day, independent of the host zone. Medplum
    // indexes a date-only occurrenceDateTime (CAIR RXA-3 without a time) at UTC
    // midnight, so a clinic-local window starting at 07:00Z would miss those
    // records and CAIR's ifNoneExist would create duplicates. Elation sends UTC
    // instants (`...Z`), which also resolve to their UTC day.
    const start = startOfDateInUTC(date)
    const end = addHours(start, 24).toISOString()
    return {
      patient,
      _filter: `date ge ${start} and date lt ${end}`,
    }
  }

  static byPatient(patient: Reference<Patient>): QueryTypes {
    return {
      patient,
      status: 'completed',
      _sort: '-date',
      _count: '100',
    }
  }

  static isInstance(value: unknown): value is Immunization {
    return isResource(value) && isOfType(value, 'Immunization')
  }

  // VFC eligibility extension
  @Extension({
    url: IMMUNIZATION_VFC_ELIGIBILITY_EXTENSION,
    kind: 'valueString',
  })
  vfcEligibility?: string

  // Funding source extension
  @Extension({
    url: IMMUNIZATION_FUNDING_SOURCE_EXTENSION,
    kind: 'valueString',
  })
  fundingSource?: string

  // Information source extension
  @Extension({
    url: IMMUNIZATION_INFORMATION_SOURCE_EXTENSION,
    kind: 'valueString',
  })
  informationSource?: string

  // Publicity code extension
  @Extension({
    url: IMMUNIZATION_PUBLICITY_CODE_EXTENSION,
    kind: 'valueString',
  })
  publicityCode?: string

  // Patient consent extension
  @Extension({
    url: IMMUNIZATION_PATIENT_CONSENT_EXTENSION,
    kind: 'valueBoolean',
  })
  patientConsent?: boolean

  // Allowed sharing extension
  @Extension({
    url: IMMUNIZATION_ALLOWED_SHARING_EXTENSION,
    kind: 'valueBoolean',
  })
  allowedSharing?: boolean

  // Priority population extension
  @Extension({
    url: IMMUNIZATION_PRIORITY_POPULATION_EXTENSION,
    kind: 'valueString',
  })
  priorityPopulation?: string

  // NDC extension
  @Extension({
    url: IMMUNIZATION_NDC_EXTENSION,
    kind: 'valueString',
  })
  ndc?: string

  // Barcode extension (GS1 DataMatrix or other vaccine barcode)
  @Extension({
    url: IMMUNIZATION_BARCODE_EXTENSION,
    kind: 'valueString',
  })
  barcode?: string

  // CAIR submission result extension
  @Extension({
    url: IMMUNIZATION_CAIR_SUBMISSION_EXTENSION,
    kind: 'valueString',
  })
  cairSubmissionResult?: string

  // Dose validity extension
  @Extension({
    url: IMMUNIZATION_DOSE_VALIDITY_EXTENSION,
    kind: 'valueCodeableConcept',
  })
  doseValidity?: CodeableConcept

  // Dose validity reason extension (LOINC 30982-3)
  @Extension({
    url: IMMUNIZATION_DOSE_VALIDITY_REASON_EXTENSION,
    kind: 'valueString',
  })
  doseValidityReason?: string

  // Series extension
  @Extension({
    url: IMMUNIZATION_SERIES_EXTENSION,
    kind: 'valueString',
  })
  series?: string

  // Raw HL7 segments for debugging
  @Extension({
    url: IMMUNIZATION_RAW_HL7_EXTENSION,
    kind: 'valueString',
  })
  rawHl7Segments?: string

  // Computed properties
  get isCompleted(): boolean {
    return this.resource.status === 'completed'
  }

  get isEnteredInError(): boolean {
    return this.resource.status === 'entered-in-error'
  }

  get isNotDone(): boolean {
    return this.resource.status === 'not-done'
  }

  get patient(): Reference<Patient> | undefined {
    return this.resource.patient
  }

  get status(): string | undefined {
    return this.resource.status
  }

  get statusReason(): CodeableConcept | undefined {
    return this.resource.statusReason
  }

  set statusReason(reason: CodeableConcept) {
    this.resource.statusReason = reason
  }

  get refusalReason(): string | undefined {
    return this.resource.statusReason?.text
  }

  set refusalReason(reason: string) {
    this.resource.statusReason = { text: reason }
  }

  get performers(): {
    function: string | undefined
    actor: Reference<Practitioner | PractitionerRole | Organization>
  }[] {
    return (this.resource.performer ?? []).map((performer) => ({
      function:
        performer.function?.coding?.[0]?.display ?? performer.function?.text,
      actor: performer.actor,
    }))
  }

  get orderingPractitioner():
    | Reference<Practitioner | PractitionerRole | Organization>
    | undefined {
    return this.resource.performer?.find(
      (p) =>
        p.function?.coding?.[0]?.code === 'OP' ||
        p.function?.text?.toLowerCase().includes('order')
    )?.actor
  }

  get administeringPractitioner():
    | Reference<Practitioner | PractitionerRole | Organization>
    | undefined {
    return this.resource.performer?.find(
      (p) =>
        p.function?.coding?.[0]?.code === 'AP' ||
        p.function?.text?.toLowerCase().includes('administer')
    )?.actor
  }

  /** Undefined when the administering performer is display-only, as on CAIR-imported doses. */
  get administeringPractitionerReference():
    | Reference<Practitioner>
    | undefined {
    const actor = this.administeringPractitioner
    return isOfType(actor, 'Practitioner') && actor.reference
      ? actor
      : undefined
  }

  get vaccineCode(): CodeableConcept | undefined {
    return this.resource.vaccineCode
  }

  set vaccineCode(coding: CodingValue) {
    this.resource.vaccineCode = {
      coding: [coding],
    }
  }

  addVaccineCode(coding: CodingValue): void {
    this.resource.vaccineCode?.coding?.push(coding)
  }

  get vaccineDisplay(): string | undefined {
    return this.resource.vaccineCode?.coding?.[0]?.display
  }

  get administeredDate(): string | undefined {
    return this.resource.occurrenceDateTime
  }

  set administeredDate(administeredDate: string | undefined) {
    this.resource.occurrenceDateTime = administeredDate
  }

  // Historical records (CAIR HL7, Elation) carry a full timestamp in
  // `occurrenceDateTime`; date pickers need the calendar day only. Slice rather
  // than reparse so the day is read as written — parsing through the browser
  // timezone shifts pre-dawn UTC timestamps onto the previous day.
  get administeredDateString(): DateString | null {
    const day = this.resource.occurrenceDateTime?.slice(0, 10)
    return day && isDateString(day) ? day : null
  }

  get expirationDate(): string | undefined {
    return this.resource.expirationDate
  }

  get lotNumber(): string | undefined {
    return this.resource.lotNumber
  }

  get manufacturer(): string | undefined {
    return this.resource.manufacturer?.display
  }

  set site(site: CodeableConcept) {
    this.resource.site = site
  }

  get site(): string | undefined {
    return this.resource.site?.text
  }

  get route(): string | undefined {
    return this.resource.route?.text
  }

  set route(route: CodeableConcept) {
    this.resource.route = route
  }

  get doseQuantity(): { value: number; unit: string } | undefined {
    const quantity = this.resource.doseQuantity
    if (!quantity?.value) {
      return undefined
    }
    return {
      value:
        typeof quantity.value === 'number'
          ? quantity.value
          : parseFloat(quantity.value),
      unit: quantity.unit ?? 'ml',
    }
  }

  get reasonText(): string | undefined {
    return this.resource.reasonCode?.[0]?.text
  }

  set reasonText(reasonText: string) {
    this.resource.reasonCode = reasonText ? [{ text: reasonText }] : undefined
  }

  // Clinician notes exclude the entered-in-error reason, so editing Notes never
  // drops the reason a correction was recorded with.
  get notes(): string | undefined {
    return this.resource.note?.find((n) => !isEnteredInErrorReason(n))?.text
  }

  set notes(notes: string) {
    const reasons = (this.resource.note ?? []).filter(isEnteredInErrorReason)
    const next = notes ? [{ text: notes }, ...reasons] : reasons
    this.resource.note = next.length > 0 ? next : undefined
  }

  get enteredInErrorReason(): Annotation | undefined {
    return this.resource.note?.filter(isEnteredInErrorReason).at(-1)
  }

  get enteredInErrorAuthor(): Reference<Practitioner> | undefined {
    const author = this.enteredInErrorReason?.authorReference
    return isOfType(author, 'Practitioner') ? author : undefined
  }

  get visDocumentType(): string | undefined {
    return this.resource.education?.[0]?.documentType
  }

  get isPrimarySource(): boolean {
    return this.resource.primarySource === true
  }

  get doseNumber(): number | undefined {
    return this.resource.protocolApplied?.[0]?.doseNumberPositiveInt
  }

  // Method to mark as completed
  markCompleted(): void {
    this.resource.status = 'completed'
  }

  markEnteredInError(reason?: {
    text: string
    author: Reference<Practitioner>
    time: string
  }): void {
    this.resource.status = 'entered-in-error'
    if (reason) {
      this.resource.note = [
        ...(this.resource.note ?? []),
        {
          extension: [
            {
              url: IMMUNIZATION_ENTERED_IN_ERROR_REASON_EXTENSION,
              valueBoolean: true,
            },
          ],
          text: reason.text,
          authorReference: reason.author,
          time: reason.time,
        },
      ]
    }
  }

  // Method to mark as not done
  markNotDone(): void {
    this.resource.status = 'not-done'
  }

  // Method to mark as refused
  markRefused(reason?: string): void {
    this.resource.status = 'not-done'
    if (reason) {
      this.refusalReason = reason
    }
  }

  setDoseNumber(doseNumber: number, seriesDoses?: number): void {
    this.resource.protocolApplied ??= []
    this.resource.protocolApplied[0] ??= {}
    this.resource.protocolApplied[0].doseNumberPositiveInt = doseNumber
    if (seriesDoses !== undefined) {
      this.resource.protocolApplied[0].seriesDosesPositiveInt = seriesDoses
    }
  }

  setSeriesDoses(doseNumber: number, seriesDoses: number): void {
    this.setDoseNumber(doseNumber, seriesDoses)
  }

  addProtocolExtension(extension: FHIRExtension): void {
    // Only add extension if protocolApplied already exists with a doseNumber
    // FHIR requires doseNumber when protocolApplied exists
    if (
      this.resource.protocolApplied?.[0] &&
      (this.resource.protocolApplied[0].doseNumberPositiveInt !== undefined ||
        this.resource.protocolApplied[0].doseNumberString !== undefined)
    ) {
      this.resource.protocolApplied[0].extension ??= []
      this.resource.protocolApplied[0].extension.push(extension)
    }
  }

  set reaction(reaction: { display: string }) {
    this.resource.reaction ??= []
    this.resource.reaction[0] ??= {}
    this.resource.reaction[0].detail = {
      display: reaction.display,
    }
  }
}
